'use client';

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import { parsePhoneNumber } from 'libphonenumber-js';
import debounce from 'lodash.debounce';
import { DataGrid, FilterSearch, PermissionTooltipWrapperButton } from '@repo/components';
import { BlocklistApiResponse, PERMISSIONS } from '@repo/shared/domain';
import {
	EventEmitter,
	getVerifyPermission,
	guardedClient as guardedRequest,
	sortToString,
	stringToSort,
	constructQuery,
} from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import BlocklistForm from './Form';

interface BlocklistSearch {
	sort?: string;
	page?: number;
	search?: string;
}

const getBlocklist = async (query: string): Promise<BlocklistApiResponse> => {
	const { body } = await guardedRequest.get<undefined, BlocklistApiResponse>(`/api/blockList?${query}`, {
		schema: undefined,
	});
	return body;
};

export const Route = createFileRoute('/_admin/config/blocklist/')({
	validateSearch: (search: Record<string, unknown>): BlocklistSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
		search: search.search as string | undefined,
	}),
	beforeLoad: ({ search }) => {
		if (!search.sort) {
			throw redirect({
				to: '/config/blocklist',
				search: { sort: 'firstName:asc' },
			});
		}
	},
	component: BlocklistPage,
});

// An entry can only ever match a person if it carries a usable identifier:
// email, phone, ID number, a direct user link, or a full name with date of birth.
function isMatchable(row: {
	email?: string;
	phone?: string;
	idNumber?: string;
	userId?: string;
	firstName?: string;
	lastName?: string;
	dateOfBirth?: string | Date;
}): boolean {
	return (
		!!row.email ||
		!!row.phone ||
		!!row.idNumber ||
		!!row.userId ||
		(!!row.firstName && !!row.lastName && !!row.dateOfBirth)
	);
}

const columns = [
	{
		field: 'firstName',
		headerName: 'First name',
		width: 150,
	},
	{
		field: 'lastName',
		headerName: 'Last name',
		width: 150,
	},
	{
		field: 'email',
		headerName: 'Email',
		flex: 1,
	},
	{
		field: 'phone',
		headerName: 'Phone',
		flex: 1,
		valueFormatter: (params: { value: string }) => {
			try {
				if (params.value) return parsePhoneNumber(params.value)?.formatInternational();
			} catch (error) {
				console.log(error);
				return params.value;
			}
		},
	},
	{
		field: 'dateOfBirth',
		headerName: 'Date of Birth',
		flex: 1,
		valueFormatter: (params: { value: string }) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'idNumber',
		headerName: 'ID Number',
		flex: 1,
	},
	{
		field: 'expiresAt',
		headerName: 'Expires At',
		flex: 1,
		valueFormatter: (params: { value: string }) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'source',
		headerName: 'Source',
		width: 100,
		valueFormatter: (params: { value?: string }) => (params.value === 'screening' ? 'Screening' : 'Manual'),
	},
	{
		field: 'status',
		headerName: 'Status',
		width: 110,
		sortable: false,
		renderCell: (c: {
			row: {
				email?: string;
				phone?: string;
				idNumber?: string;
				userId?: string;
				firstName?: string;
				lastName?: string;
				dateOfBirth?: string | Date;
			};
		}) =>
			isMatchable(c.row) ? null : (
				<Tooltip title="Won't match anyone: add an email, phone, ID number, or a full name with date of birth.">
					<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Inactive</span>
				</Tooltip>
			),
	},
];

function BlocklistPage() {
	const searchParams = Route.useSearch();
	const { sort, page, search } = searchParams;
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const [isFormOpened, setIsFormOpened] = useState(false);

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Blocklist');

	const query = constructQuery({ sort, page, search, noPagination: 'false' });

	const { data: blocklistData, isLoading } = useQuery({
		queryKey: ['blocklist', query],
		queryFn: () => getBlocklist(query),
	});

	const blocklist = blocklistData || { data: [], count: 0, page: 0, pageSize: 15 };

	const handleSort = ([sortItem]: { field: string; sort: 'asc' | 'desc' }[]) => {
		updateQueryParams({ sort: sortToString(sortItem) });
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const handleDelete = async (blockListItemId: string) => {
		try {
			await guardedRequest.del(`/api/blockList/${blockListItemId}`);
			revalidateBlocklist();
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to delete block list item.',
			});
		}
	};

	const handleCreate = async (formData: Record<string, unknown>) => {
		try {
			await guardedRequest.post('/api/blockList/', formData);
			setIsFormOpened(false);
			revalidateBlocklist();
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to create block list item.',
			});
		}
	};

	const handleCloseForm = useCallback(() => {
		setIsFormOpened(false);
	}, []);

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const handleSearchChange = (searchInput: string) => {
		updateQueryParams({ search: searchInput });
	};

	const revalidateBlocklist = () => {
		queryClient.invalidateQueries({ queryKey: ['blocklist'] });
	};

	const columnActions = [
		{
			field: 'id',
			headerName: 'Actions',
			alignItems: 'flex-end',
			renderCell: (c: { row: { id: string; source?: string } }) => {
				const isScreening = c.row.source !== 'manual';
				const deleteButton = (
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							if (!isScreening) handleDelete(c.row.id);
						}}
						disabled={isScreening}
					>
						<DeleteIcon />
					</IconButton>
				);
				return isScreening ? (
					<Tooltip title="Screening entries are managed automatically and cannot be removed manually">
						<span>{deleteButton}</span>
					</Tooltip>
				) : (
					deleteButton
				);
			},
			sortable: false,
		},
	];

	return (
		<>
			<div className="p-4">
				<div className="flex justify-between gap-2">
					<FilterSearch
						key="search"
						label="Search Blocklist"
						defaultValue={search || ''}
						onChange={debounce((searchInput: string) => {
							handleSearchChange(searchInput);
						}, 350)}
						sx={{ width: '300px' }}
					/>
					<PermissionTooltipWrapperButton
						condition={!verifyPermission(PERMISSIONS.adminSecurityBlock)}
						buttonProps={{
							onClick: handleOpenForm,
							startIcon: <AddIcon />,
							variant: 'outlined',
						}}
						key="add"
					>
						Add Item
					</PermissionTooltipWrapperButton>
				</div>
				<div className="w-full">
					<Alert sx={{ marginTop: 2 }} severity="info">
						A user is blocked when their email, phone, or ID number matches a blocklist item, or when first name,
						last name and date of birth all match. A name without a date of birth no longer blocks, to avoid false
						matches. Screening hits from the Person API (AIGIS and other sources) are added here automatically.
					</Alert>
				</div>
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						isLoading={isLoading}
						data={blocklist.data}
						columns={[...columns, ...columnActions]}
						sortingMode="server"
						onSortModelChange={handleSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(sort || 'firstName:asc')]}
						onReload={revalidateBlocklist}
						paginationMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						paginationModel={{
							page: page || 0,
							pageSize: 15,
						}}
						pageSize={blocklist.pageSize}
						rowCount={blocklist.count}
						page={blocklist.page}
					/>
				</Paper>
			</div>

			<BlocklistForm isOpen={isFormOpened} onClose={handleCloseForm} onSubmit={handleCreate} />
		</>
	);
}
